import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Search, Trash2, Wheat, Download, Info, RefreshCw } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { downloadAsCsv } from '@/lib/csv-export';
import { useEffectivePermissions } from '@/hooks/useEffectivePermissions';
import { useLabel } from '@/lib/labels';

/** ローカルタイムゾーンで今日の日付 (YYYY-MM-DD)。UTC を使うと JST 0-9 時に前日日付になる問題を回避 */
const todayLocal = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

type Store = { store_id: string; store_code: string; store_name: string };

type CostCategory = { cost_category_id: string; category_name: string; display_order: number };

type Ingredient = {
  ingredient_id: string;
  store_id: string;
  ingredient_code: string;
  ingredient_name: string;
  unit: string | null;
  unit_price: number | null;
  cost_category_id: string;
  cost_category_name: string | null;
  effective_from: string;
  effective_to: string | null;
};

type FormState = {
  ingredient_id?: string;
  store_id: string;
  ingredient_code: string;
  ingredient_name: string;
  unit: string;
  unit_price: string;
  cost_category_id: string;
  effective_from: string;
  effective_to: string;
};

/** コードの半角英数字・ハイフン・アンダースコアのみ許容 (バックエンドの halfWidthCodeSchema と同じ規則) */
const CODE_PATTERN = '[A-Za-z0-9_-]+';

type ZeroPricePreview = {
  master_unit_price: number | null;
  updatable_count: number;
  unresolvable_count: number;
  preview: Array<{ delivery_date: string; vendor_name: string | null; quantity: number; new_amount: number }>;
};

const emptyForm = (): FormState => ({
  store_id: '',
  ingredient_code: '',
  ingredient_name: '',
  unit: '',
  unit_price: '',
  cost_category_id: '',
  effective_from: todayLocal(),
  effective_to: '',
});

export function IngredientsPage() {
  const t = useLabel();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.roles?.some((r) => r === 'company_admin' || r === 'platform_admin') ?? false;
  // v0.3 (S-11 DL権限管理): export:view権限が無いユーザーにはCSV出力ボタンを非表示にする
  const { hasPermissionForFeatureAction } = useEffectivePermissions();
  const canExport = hasPermissionForFeatureAction('export', 'view');

  const { data: ingredients = [], isLoading } = useQuery<Ingredient[]>({
    queryKey: ['ingredients'],
    queryFn: () => apiFetch<Ingredient[]>('/ingredients'),
  });
  const { data: stores = [], isLoading: storesLoading } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/stores'),
  });
  const { data: costCategories = [] } = useQuery<CostCategory[]>({
    queryKey: ['ingredients-cost-categories'],
    queryFn: () => apiFetch<CostCategory[]>('/ingredients/cost-categories'),
  });

  const [editing, setEditing] = useState<FormState | null>(null);
  const [deleting, setDeleting] = useState<Ingredient | null>(null);
  const [backfilling, setBackfilling] = useState<Ingredient | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterStore, setFilterStore] = useState<string>('all');

  const backfillPreviewQuery = useQuery<ZeroPricePreview>({
    queryKey: ['ingredient-zero-price-preview', backfilling?.ingredient_id],
    queryFn: () => apiFetch<ZeroPricePreview>(`/ingredients/${backfilling!.ingredient_id}/zero-price-deliveries`),
    enabled: backfilling !== null,
  });

  const storeName = (id: string) => stores.find((s) => s.store_id === id)?.store_name ?? id;

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return ingredients.filter((i) => {
      if (filterStore !== 'all' && i.store_id !== filterStore) return false;
      if (q && !i.ingredient_name.toLowerCase().includes(q) && !i.ingredient_code.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [ingredients, searchText, filterStore]);

  // グルーピング: 店舗ごとにまとめて表示 (食材は店舗単位の独立マスタのため)
  const groupedByStore = useMemo(() => {
    const map = new Map<string, Ingredient[]>();
    for (const i of filtered) {
      if (!map.has(i.store_id)) map.set(i.store_id, []);
      map.get(i.store_id)!.push(i);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.ingredient_name.localeCompare(b.ingredient_name) || a.effective_from.localeCompare(b.effective_from));
    }
    return Array.from(map.entries()).sort(([a], [b]) => storeName(a).localeCompare(storeName(b)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, stores]);

  const createMut = useMutation({
    mutationFn: (form: FormState) => apiFetch('/ingredients', {
      method: 'POST',
      body: JSON.stringify({
        store_id: form.store_id,
        ingredient_code: form.ingredient_code,
        ingredient_name: form.ingredient_name,
        unit: form.unit || undefined,
        unit_price: form.unit_price === '' ? undefined : Number(form.unit_price),
        cost_category_id: form.cost_category_id,
        effective_from: form.effective_from,
        effective_to: form.effective_to || null,
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ingredients'] });
      setEditing(null);
      toast.success('食材を追加しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '追加に失敗しました'),
  });

  const updateMut = useMutation({
    mutationFn: (form: FormState) => apiFetch(`/ingredients/${form.ingredient_id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        ingredient_name: form.ingredient_name,
        unit: form.unit || null,
        unit_price: form.unit_price === '' ? null : Number(form.unit_price),
        cost_category_id: form.cost_category_id,
        effective_from: form.effective_from,
        effective_to: form.effective_to || null,
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ingredients'] });
      setEditing(null);
      toast.success('更新しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/ingredients/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ingredients'] });
      setDeleting(null);
      toast.success('削除しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'),
  });

  const backfillMut = useMutation({
    mutationFn: (id: string) => apiFetch<{ updated_count: number }>(`/ingredients/${id}/zero-price-deliveries/backfill`, { method: 'POST' }),
    onSuccess: (data) => {
      toast.success(`${data.updated_count} 件の納品明細を更新しました（原価ダッシュボードの「コスト分類別原価」に反映されます）`);
      setBackfilling(null);
      // コスト分類別原価等、delivery_details の集計に依存するダッシュボードのキャッシュを無効化
      qc.invalidateQueries({ queryKey: ['data-summary'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '一括更新に失敗しました'),
  });

  const submitForm = () => {
    if (!editing) return;
    if (!editing.store_id || (!editing.ingredient_id && !editing.ingredient_code) || !editing.ingredient_name || !editing.cost_category_id || !editing.effective_from) {
      toast.error('店舗・食材コード・食材名・コスト分類・開始日は必須です');
      return;
    }
    if (editing.effective_to && editing.effective_from > editing.effective_to) {
      toast.error('開始日は終了日より前の日付にしてください');
      return;
    }
    if (editing.ingredient_id) updateMut.mutate(editing);
    else createMut.mutate(editing);
  };

  const hasAnyFilter = Boolean(searchText || filterStore !== 'all');

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-h1 flex items-center gap-2">
            <Wheat size={24} className="text-brand-red" /> {t('entity.ingredient_master', '食材マスタ')}
          </h1>
          <p className="text-sm text-neutral-sub mt-1">
            店舗ごとの食材と、原価コスト分類を管理します。納品明細の取込・手入力はここに登録された食材コードを参照します
          </p>
          <p className="text-xs text-neutral-sub mt-1 flex items-start gap-1">
            <Info size={13} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span>
              「単価」は、麺・米などの定常仕入れ品で納品明細の単価・金額が未入力の場合の<strong>既定値（フォールバック）</strong>として使われます (単価×数量で自動算出)。
              実際の納品書に単価・金額の記載があれば、そちらが常に優先されます。仕入価格が変動した際はここで更新してください
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canExport && (
          <button
            type="button"
            disabled={isLoading || storesLoading || filtered.length === 0}
            aria-label="現在の絞込結果をマスタ取込CSV形式で出力"
            onClick={() => {
              const storeCodeMap = new Map(stores.map((s) => [s.store_id, s.store_code]));
              const skipped: Ingredient[] = [];
              const rows: Array<Record<string, unknown>> = [];
              for (const i of filtered) {
                const code = storeCodeMap.get(i.store_id);
                if (!code) { skipped.push(i); continue; }
                rows.push({
                  store_code: code,
                  ingredient_code: i.ingredient_code,
                  ingredient_name: i.ingredient_name,
                  unit: i.unit ?? '',
                  unit_price: i.unit_price ?? '',
                  cost_category_name: i.cost_category_name ?? '',
                  effective_from: i.effective_from,
                  effective_to: i.effective_to ?? '',
                });
              }
              if (rows.length === 0) { toast.error('出力対象の行がありません'); return; }
              const headers = ['store_code', 'ingredient_code', 'ingredient_name', 'unit', 'unit_price', 'cost_category_name', 'effective_from', 'effective_to'];
              downloadAsCsv(`ingredient_master_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
              if (skipped.length > 0) toast.error(`店舗コードを解決できず ${skipped.length} 件を除外しました`);
            }}
            className="flex items-center gap-1 min-h-[36px] px-3 py-2 rounded border border-neutral-line text-sm hover:bg-neutral-panel disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            title={
              storesLoading ? '店舗一覧を読み込み中…'
                : filtered.length === 0 ? (hasAnyFilter ? '絞込みを解除してください' : '出力対象がありません')
                : 'マスタ取込 CSV 形式で出力 (再取込可)'
            }
          >
            <Download size={14} /> CSV出力
          </button>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={() => setEditing(emptyForm())}
              className="flex items-center gap-1 min-h-[36px] px-3 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover whitespace-nowrap"
            >
              <Plus size={16} /> 新規追加
            </button>
          )}
        </div>
      </div>

      {/* フィルタバー */}
      <div className="border border-neutral-line rounded-lg bg-white p-3 flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label htmlFor="ing-search" className="block text-xs text-neutral-sub mb-1">食材名・コードで検索</label>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-sub" aria-hidden="true" />
            <input
              id="ing-search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="例: 豚肩ロース"
              className="w-full pl-8 pr-3 py-1.5 border border-neutral-line rounded text-sm focus-ring"
            />
          </div>
        </div>
        <div>
          <label htmlFor="ing-filter-store" className="block text-xs text-neutral-sub mb-1">店舗</label>
          <select
            id="ing-filter-store"
            value={filterStore}
            onChange={(e) => setFilterStore(e.target.value)}
            className="px-2 py-1.5 border border-neutral-line rounded text-sm focus-ring"
          >
            <option value="all">全店舗</option>
            {stores.map((s) => <option key={s.store_id} value={s.store_id}>{s.store_name}</option>)}
          </select>
        </div>
        {hasAnyFilter && (
          <button
            type="button"
            onClick={() => { setSearchText(''); setFilterStore('all'); }}
            className="min-h-[36px] px-3 py-1.5 text-sm text-neutral-sub hover:text-brand-red"
          >
            クリア
          </button>
        )}
      </div>

      {/* 一覧 (店舗ごとにグルーピング) */}
      {isLoading ? (
        <div className="text-neutral-sub">読み込み中…</div>
      ) : groupedByStore.length === 0 ? (
        <div className="p-8 text-center text-neutral-sub border border-neutral-line rounded-lg bg-white">
          {hasAnyFilter ? '条件に一致する食材がありません' : '登録されている食材がありません'}
        </div>
      ) : (
        <div className="space-y-3">
          {groupedByStore.map(([storeId, list]) => (
            <section key={storeId} className="border border-neutral-line rounded-lg bg-white overflow-hidden">
              <div className="px-4 py-2.5 border-b border-neutral-line bg-neutral-panel flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Wheat size={16} className="text-brand-orange" />
                  <span className="font-medium">{storeName(storeId)}</span>
                  <span className="text-xs text-neutral-sub">({list.length} 件)</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="text-neutral-sub bg-neutral-panel/50 text-left">
                    <tr>
                      <th scope="col" className="px-3 py-2 w-28">{t('field.ingredient_master.ingredient_code', 'コード')}</th>
                      <th scope="col" className="px-3 py-2 min-w-[8rem]">{t('field.ingredient_master.ingredient_name', '食材名')}</th>
                      <th scope="col" className="px-3 py-2 w-24">{t('field.ingredient_master.unit', '単位')}</th>
                      <th scope="col" className="px-3 py-2 w-24" title="納品明細で単価・金額が未入力の場合の自動算出に使用">{t('field.ingredient_master.unit_price', '単価')}</th>
                      <th scope="col" className="px-3 py-2 min-w-[6rem]">{t('field.ingredient_master.cost_category_id', 'コスト分類')}</th>
                      <th scope="col" className="px-3 py-2 w-28">{t('field.ingredient_master.effective_from', '開始日')}</th>
                      <th scope="col" className="px-3 py-2 w-28">{t('field.ingredient_master.effective_to', '終了日')}</th>
                      {isAdmin && <th scope="col" className="px-3 py-2 w-20 text-right">操作</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((i) => (
                      <tr key={i.ingredient_id} className="border-t border-neutral-line hover:bg-neutral-panel/40">
                        <td className="px-3 py-2 font-mono text-xs text-neutral-sub">{i.ingredient_code}</td>
                        <td className="px-3 py-2">{i.ingredient_name}</td>
                        <td className="px-3 py-2 text-neutral-sub">{i.unit ?? '—'}</td>
                        <td className="px-3 py-2 tabular-nums">{i.unit_price != null ? i.unit_price.toLocaleString() : '—'}</td>
                        <td className="px-3 py-2 text-neutral-sub">{i.cost_category_name ?? '—'}</td>
                        <td className="px-3 py-2 tabular-nums">{i.effective_from}</td>
                        <td className="px-3 py-2 tabular-nums text-neutral-sub">{i.effective_to ?? '—'}</td>
                        {isAdmin && (
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            {i.unit_price != null && i.unit_price > 0 && (
                              <button
                                type="button"
                                onClick={() => setBackfilling(i)}
                                className="p-1.5 text-neutral-sub hover:text-brand-orange"
                                aria-label="0円の納品明細をこの単価で一括更新"
                                title="0円の納品明細をこの単価で一括更新"
                              >
                                <RefreshCw size={14} />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setEditing({
                                ingredient_id: i.ingredient_id,
                                store_id: i.store_id,
                                ingredient_code: i.ingredient_code,
                                ingredient_name: i.ingredient_name,
                                unit: i.unit ?? '',
                                unit_price: i.unit_price != null ? String(i.unit_price) : '',
                                cost_category_id: i.cost_category_id,
                                effective_from: i.effective_from,
                                effective_to: i.effective_to ?? '',
                              })}
                              className="p-1.5 text-neutral-sub hover:text-brand-red"
                              aria-label="編集"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleting(i)}
                              className="p-1.5 text-neutral-sub hover:text-warning-text"
                              aria-label="削除"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}

      {/* 追加・編集モーダル */}
      <Modal
        open={editing !== null}
        title={editing?.ingredient_id ? '食材を編集' : '食材を追加'}
        size="sm"
        onClose={() => setEditing(null)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="px-4 py-2 rounded border border-neutral-line text-sm hover:bg-neutral-panel"
            >
              キャンセル
            </button>
            <button
              type="button"
              onClick={submitForm}
              disabled={createMut.isPending || updateMut.isPending}
              className="flex items-center gap-1 px-4 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover disabled:opacity-50"
            >
              {editing?.ingredient_id ? '保存' : '追加'}
            </button>
          </>
        }
      >
        {editing && (
          <div className="space-y-3">
            <div>
              <label htmlFor="ing-store" className="block text-sm text-neutral-sub mb-1">{t('field.ingredient_master.store_id', '店舗')} *</label>
              <select
                id="ing-store"
                value={editing.store_id}
                onChange={(e) => setEditing({ ...editing, store_id: e.target.value })}
                disabled={!!editing.ingredient_id}
                className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring disabled:bg-neutral-panel"
              >
                <option value="">選択してください</option>
                {stores.map((s) => <option key={s.store_id} value={s.store_id}>{s.store_name}</option>)}
              </select>
              {editing.ingredient_id && (
                <p className="text-xs text-neutral-sub mt-1">店舗は編集できません (新規追加時のみ選択可)</p>
              )}
            </div>
            <div>
              <label htmlFor="ing-code" className="block text-sm text-neutral-sub mb-1">{t('field.ingredient_master.ingredient_code', '食材コード')} *</label>
              <input
                id="ing-code"
                value={editing.ingredient_code}
                onChange={(e) => setEditing({ ...editing, ingredient_code: e.target.value })}
                disabled={!!editing.ingredient_id}
                pattern={CODE_PATTERN}
                placeholder="例: ING-120-001"
                maxLength={30}
                className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring disabled:bg-neutral-panel"
              />
              {editing.ingredient_id ? (
                <p className="text-xs text-neutral-sub mt-1">コードは編集できません</p>
              ) : (
                <p className="text-xs text-neutral-sub mt-1">半角英数字・ハイフン・アンダースコアのみ</p>
              )}
            </div>
            <div>
              <label htmlFor="ing-name" className="block text-sm text-neutral-sub mb-1">{t('field.ingredient_master.ingredient_name', '食材名')} *</label>
              <input
                id="ing-name"
                value={editing.ingredient_name}
                onChange={(e) => setEditing({ ...editing, ingredient_name: e.target.value })}
                placeholder="例: 豚肩ロース"
                maxLength={100}
                className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="ing-unit" className="block text-sm text-neutral-sub mb-1">{t('field.ingredient_master.unit', '単位')}</label>
                <input
                  id="ing-unit"
                  value={editing.unit}
                  onChange={(e) => setEditing({ ...editing, unit: e.target.value })}
                  placeholder="例: kg"
                  maxLength={20}
                  className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring"
                />
              </div>
              <div>
                <label htmlFor="ing-unit-price" className="block text-sm text-neutral-sub mb-1">{t('field.ingredient_master.unit_price', '単価')}（税抜、円）</label>
                <input
                  id="ing-unit-price"
                  type="number"
                  min={0}
                  value={editing.unit_price}
                  onChange={(e) => setEditing({ ...editing, unit_price: e.target.value })}
                  placeholder="例: 1200"
                  className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring"
                />
                <p className="text-xs text-neutral-sub mt-1">納品明細で単価・金額が未入力の場合の自動算出に使用</p>
              </div>
            </div>
            <div>
              <label htmlFor="ing-cost-category" className="block text-sm text-neutral-sub mb-1">{t('field.ingredient_master.cost_category_id', 'コスト分類')} *</label>
              <select
                id="ing-cost-category"
                value={editing.cost_category_id}
                onChange={(e) => setEditing({ ...editing, cost_category_id: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring"
              >
                <option value="">選択してください</option>
                {costCategories.map((c) => <option key={c.cost_category_id} value={c.cost_category_id}>{c.category_name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="ing-effective-from" className="block text-sm text-neutral-sub mb-1">{t('field.ingredient_master.effective_from', '開始日')} *</label>
                <input
                  id="ing-effective-from"
                  type="date"
                  value={editing.effective_from}
                  onChange={(e) => setEditing({ ...editing, effective_from: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring"
                />
              </div>
              <div>
                <label htmlFor="ing-effective-to" className="block text-sm text-neutral-sub mb-1">{t('field.ingredient_master.effective_to', '終了日')}</label>
                <input
                  id="ing-effective-to"
                  type="date"
                  value={editing.effective_to}
                  onChange={(e) => setEditing({ ...editing, effective_to: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-line rounded text-sm focus-ring"
                />
                <p className="text-xs text-neutral-sub mt-1">空欄=継続中</p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* 削除確認 */}
      <ConfirmDialog
        open={deleting !== null}
        title="食材の削除"
        destructive
        confirmLabel="削除する"
        loading={deleteMut.isPending}
        onClose={() => { if (!deleteMut.isPending) setDeleting(null); }}
        onConfirm={() => { if (deleting) deleteMut.mutate(deleting.ingredient_id); }}
        message={deleting && (
          <div className="space-y-2">
            <p>
              以下の食材を削除します。この操作は<strong className="text-danger-text">取り消せません</strong>。
            </p>
            <div className="p-3 rounded bg-neutral-panel border border-neutral-line text-sm space-y-1">
              <div><span className="text-neutral-sub">店舗：</span>{storeName(deleting.store_id)}</div>
              <div><span className="text-neutral-sub">コード：</span><span className="font-mono">{deleting.ingredient_code}</span></div>
              <div><span className="text-neutral-sub">食材名：</span>{deleting.ingredient_name}</div>
              <div><span className="text-neutral-sub">期間：</span>{deleting.effective_from} 〜 {deleting.effective_to ?? '継続中'}</div>
            </div>
            <p className="text-xs text-neutral-sub">
              既に納品明細データで参照されている場合は削除できません。その場合は終了日設定で対応してください。
            </p>
          </div>
        )}
      />

      {/* 0円納品明細の一括更新確認 (v0.2.1、単価マスタからのフォールバック機能の遡及適用)
          複数レコードを不可逆に書き換える操作のため destructive 扱いとし、Cancel に初期フォーカス・
          警告トーンの確認ボタンにする (3並列レビュー Design CRITICAL 反映)。対象0件・未確定時は
          confirmDisabled で実行ボタン自体を無効化する */}
      <ConfirmDialog
        open={backfilling !== null}
        title="0円の納品明細を一括更新"
        confirmLabel="更新する"
        destructive
        confirmDisabled={backfillPreviewQuery.isLoading || !backfillPreviewQuery.data || backfillPreviewQuery.data.updatable_count === 0}
        loading={backfillMut.isPending}
        onClose={() => { if (!backfillMut.isPending) setBackfilling(null); }}
        onConfirm={() => {
          if (!backfilling || !backfillPreviewQuery.data || backfillPreviewQuery.data.updatable_count === 0) return;
          backfillMut.mutate(backfilling.ingredient_id);
        }}
        message={backfilling && (
          <div className="space-y-2" aria-live="polite">
            <p>
              「<strong>{backfilling.ingredient_name}</strong>」の単価が0円のまま保存されている納品明細を、
              現在のマスタ単価（<strong>¥{backfilling.unit_price?.toLocaleString()}</strong>）×数量で再計算します。
              実際の納品書の単価が入力済みの行は対象外です（上書きされません）。
            </p>
            {backfillPreviewQuery.isLoading ? (
              <p className="text-sm text-neutral-sub">対象件数を確認中…</p>
            ) : backfillPreviewQuery.isError ? (
              <p className="text-sm text-warning-text">対象件数の取得に失敗しました</p>
            ) : backfillPreviewQuery.data && backfillPreviewQuery.data.updatable_count === 0 ? (
              <p className="text-sm text-neutral-sub">対象となる0円の納品明細はありません（数量未入力のため自動算出できない行が {backfillPreviewQuery.data.unresolvable_count} 件あります）。</p>
            ) : backfillPreviewQuery.data ? (
              <div className="p-3 rounded bg-brand-gold/10 border border-brand-gold/30 text-sm space-y-2">
                <p className="font-semibold">
                  {backfillPreviewQuery.data.updatable_count} 件を更新します
                  {backfillPreviewQuery.data.unresolvable_count > 0 && (
                    <span className="font-normal text-neutral-sub"> （数量未入力のため対象外: {backfillPreviewQuery.data.unresolvable_count} 件、手動対応が必要です）</span>
                  )}
                </p>
                <ul className="text-xs space-y-0.5 pl-4 list-disc max-h-40 overflow-y-auto">
                  {backfillPreviewQuery.data.preview.map((r, idx) => (
                    <li key={idx} className="tabular-nums">
                      {[r.delivery_date, r.vendor_name, `数量${r.quantity}`].filter(Boolean).join(' ')} → ¥{r.new_amount.toLocaleString()}
                    </li>
                  ))}
                  {backfillPreviewQuery.data.updatable_count > backfillPreviewQuery.data.preview.length && (
                    <li>他 {backfillPreviewQuery.data.updatable_count - backfillPreviewQuery.data.preview.length} 件</li>
                  )}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      />
    </div>
  );
}
